import { Button, Col, Container, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PropertyForm } from "../../../Components/Forms/PropertyForm/PropertyForm";
import useMyProperties from "../../../Hooks/UseMyProperties/UseMyPropertiesHook";
import useMyPropertiesSelectValues from "../../../Hooks/UseMyProperties/UseMyPropertySelectValuesHook";
import { accountSelector } from "../../../redux/account/accountSlice";
import { navigateTo } from "../../../redux/router/routerActions";
import { useAppDispatch } from "../../../redux/store";
import { PropertyType } from "../../../Types/propertyTypes";
import StickyBar from "../../../Components/StickyBar/StickyBar";
import { useAddNewPropertyMutation, useEditPropertyMutation, useGetPropertyByIdQuery, useImportResourceAccessMutation, useLazyGetResourceAccessListQuery } from "../../../services/bClientApi";
import useUploadImage from "../../../Hooks/UseUploadImage/useUploadImageHook";

export default function EditPropertyPage() {
  const { _id: userId, email, role } = useSelector(accountSelector)
  const {
    propertyId = ''
  } = useParams()

  const {
    data: propertyData = {},
    error,
    isLoading
  } = useGetPropertyByIdQuery(propertyId, { skip: !propertyId })

  const {
    _id = ''
  } = propertyData

  const [
    editProperty,
    {
      isLoading: isEditing,
      error: editError
    }
  ] = useEditPropertyMutation()

  const [
    addNewProperty,
    {
      isLoading: isAdding,
      error: addError
    }
  ] = useAddNewPropertyMutation()

  const {
    uploadImageFn,
    imageUploadProgress,
    uploadImageMultiFn,
    // isLoading: uploadImageLoading,
    error: uploadImageError,
  } = useUploadImage()

  const [importResourceAccess] = useImportResourceAccessMutation()
  const [getResourceAccess] = useLazyGetResourceAccessListQuery()

  const dispatch = useAppDispatch()

  const { myProperties } = useMyProperties()
  const myPropertySelectOptions = useMyPropertiesSelectValues(myProperties)

  const onSubmit = async (values: Partial<PropertyType>) => {
    if (!email || !role || !_id) {
      return
    }
    const editedProperty: any = await editProperty({ propertyId: _id, propertyInfo: values }).unwrap()
    if (editedProperty._id) {
      dispatch(navigateTo(`/property/${editedProperty._id}`))
    }
    return editedProperty
  }

  return (
    <Container>
      <Row>
        <Col>
          <h1>Edit Property</h1>
          <hr />
          {error && (<>{JSON.stringify(error)}<br /></>)}
          {editError && (<>{JSON.stringify(editError)}<br /></>)}
          {addError && (<>{JSON.stringify(addError)}<br /></>)}
          {uploadImageError && (<>{JSON.stringify(uploadImageError)}<br /></>)}


          {email && role && _id === propertyId && propertyData && !isLoading && (
            <>
              <StickyBar>
                <h4>
                  {propertyData.title.split(' - ')[0]}
                </h4>
              </StickyBar>
              <PropertyForm
                disabled={isEditing === true || isAdding === true}
                propertyOptionList={myPropertySelectOptions}
                initialValues={propertyData}
                onSubmit={onSubmit}
                uploadImageFn={uploadImageFn}
                imageUploadProgress={imageUploadProgress}
                uploadImageMultiFn={uploadImageMultiFn}
              />

              <hr />

              <Button
                variant="secondary"
                disabled={isEditing === true || isAdding === true}
                onClick={async () => {
                  const newPropertyData = {
                    ...propertyData,
                    created: new Date(),
                  }
                  delete newPropertyData._id
                  newPropertyData.title = `${newPropertyData.title} (COPY)`

                  const createdProperty = await addNewProperty(newPropertyData).unwrap()
                  if (createdProperty._id) {

                    // set up newPropertyUserList
                    const existingResourceAccess = await getResourceAccess({
                      resourceAccessQuery: {
                        resourceType: 'property',
                        resourceId: propertyData._id,
                      },
                      pagination: {
                        limit: 500,
                        page: 1
                      }
                    }).unwrap()

                    const existingResourceAccessList = existingResourceAccess?.docs || []
                    let selfFound = false
                    const newPropertyUserListCopy = existingResourceAccessList.map((user: any) => {
                      if (user.userId === userId) {
                        selfFound = true
                      }
                      return {
                        userId: user.userId,
                        role: user.role,
                      }
                    }).filter((user: any) => {
                      // remove owners from authorized users
                      return user.role !== 'owner'
                    })

                    // add self to newPropertyUserList
                    if (!selfFound) {
                      newPropertyUserListCopy.push({
                        userId: userId,
                        role: role,
                      })
                    }

                    const mappedNewPropertyUserList = newPropertyUserListCopy.map((user: any) => {
                      return {
                        resourceId: createdProperty._id,
                        resourceType: 'property',
                        userId: user.userId,
                        role: user.role,
                      }
                    })

                    await importResourceAccess(mappedNewPropertyUserList).unwrap()


                    dispatch(navigateTo(`/property/${createdProperty._id}`))
                  }
                }}
              >
                Copy Property
              </Button>


            </>
          )}
        </Col>
      </Row>
    </Container>
  )
}
